import { isUtmMedium } from './utmParameters.constants';
import type { PartialUtmParameters, UtmTelemetryKey, UtmTelemetryParams, WritableUtmParameters } from './utmParameters.types';

/**
 * `utm_source` values Joy stamps on its own internal navigation — not acquisition channels.
 * Excludes outbound-share sources (`joy`, `room_block_manager_share`) and the post-RSVP /
 * registry-confirmation sources the data team owns. Keep in sync when adding an internal CTA.
 */
export const INTERNAL_UTM_SOURCES = [
  // Guest site
  'website_accommodations_page',
  'website_travel_page',
  'website_custom_page',
  'website_cash_fund_widget',
  'website_hotel_redirect_page',
  'custom_page_booking_assistant',
  'guest_guide_booking_cta',
  'guest_guide_guest_site',
  'schedule_list_item',
  'hotel_search',
  'hotel_details',
  'hotel_joy_native_pdp',
  'hotel_confirmation',
  'guest_site_nav',
  'wedding_code_banner',
  // Admin / host surfaces that deep-link into the same hotel routes
  'admin_custom_page',
  'admin_accommodations',
  'admin_accommodations_joy_roomblock',
  'accommodations_admin_page',
  'dashboard',
  'manage_registry',
  // Honeymoon page: the promos that link to it, and the page's own links onward. Without these the
  // entry source would read as an acquisition channel and win over `honeymoon_landing` on the next
  // hop, so every search would be credited to the promo instead of the page it came through.
  'manage_registry_banner',
  'manage_registry_gift_tile',
  'manage_registry_gift_dialog',
  'track_gifts_widget',
  'track_gifts_dialog',
  'cash_funds_dashboard',
  'dashboard_nav',
  'travel_dropdown',
  'honeymoon_guided_moment',
  'honeymoon_fund_add_success',
  'admin_hotel_search_banner',
  'admin_accommodations_banner',
  'admin_accommodations_search_banner',
  // The dashboard dialog's own handoff.
  'honeymoon_search_dialog',
  'honeymoon_landing',
  // The board experience's own hops to a hotel (board card, quiz stay, Browse "Stays couples love").
  // Only those surfaces stamp them, so nothing changes for anyone without the board.
  'honeymoon_board',
  'honeymoon_quiz',
  'honeymoon_destinations',
] as const;

/** A `utm_source` value stamped by Joy's own internal navigation. */
export type InternalUtmSource = (typeof INTERNAL_UTM_SOURCES)[number];

/** Whether a `utm_source` was stamped by Joy's own internal navigation rather than an acquisition channel. */
export const isInternalUtmSource = (utmSource?: string | null): boolean => {
  return !!utmSource && (INTERNAL_UTM_SOURCES as ReadonlyArray<string>).includes(utmSource);
};

/**
 * Resolves the UTM an internal CTA should emit, per slot: inbound wins for `utm_medium`,
 * `utm_campaign`, `utm_source_secondary` and `utm_content`, but an internal `utm_source` yields to
 * the CTA's own. No equivalent classification exists for `utm_campaign` — see TRAV-3930.
 *
 * `utm_content` is forwarded unconditionally, like `utm_source_secondary`: no Joy surface mints one,
 * so any value present arrived from outside (today, an experiment arm on an abandonment link —
 * TRAV-4128). Letting an internal CTA clear it would lose the arm on the first in-app hop.
 */
export const resolveAcquisitionAwareUtm = (currentUtm: PartialUtmParameters | undefined, internalUtm: PartialUtmParameters): PartialUtmParameters => {
  const inboundSource = isInternalUtmSource(currentUtm?.utm_source) ? undefined : currentUtm?.utm_source;

  const utm_medium = currentUtm?.utm_medium || internalUtm.utm_medium;
  const utm_source = inboundSource || internalUtm.utm_source;
  const utm_campaign = currentUtm?.utm_campaign || internalUtm.utm_campaign;
  const utm_source_secondary = currentUtm?.utm_source_secondary || internalUtm.utm_source_secondary;
  const utm_content = currentUtm?.utm_content || internalUtm.utm_content;

  // Conditional spreads, not `{...internalUtm}` — an explicit `key: undefined` survives a spread and
  // callers that serialise the whole object would emit the literal string "undefined".
  return {
    ...(utm_medium && { utm_medium }),
    ...(utm_source && { utm_source }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_source_secondary && { utm_source_secondary }),
    ...(utm_content && { utm_content }),
  };
};

/**
 * Merges UTM parameters with priority: existing parameters override internal parameters.
 *
 * This function merges two sets of UTM parameters where existing parameters (typically from URL)
 * take precedence over internal parameters (typically defaults). This ensures that campaign/ads
 * tracking parameters are preserved and not overridden by internal defaults.
 *
 * @param existingUtm - UTM parameters from existing URL (highest priority - overrides internal)
 * @param internalUtm - Internal UTM parameters to use as fallback (lower priority)
 * @returns Merged UTM parameters object with existing values taking precedence
 */
export const mergeUtmParameters = (existingUtm?: PartialUtmParameters, internalUtm?: PartialUtmParameters): PartialUtmParameters => {
  // Spread internal first (base), then existing (overrides) - existing takes precedence
  return { ...internalUtm, ...existingUtm };
};

/**
 * Builds a UTM query string from the provided UTM parameters.
 * https://withjoy.atlassian.net/wiki/x/CgAw3g
 *
 * @param utm_medium - The utm_medium through which the traffic is coming (e.g. "web", "email", "sms")
 * @param utm_source - The utm_source of the traffic (e.g. "website_accommodations_page", "confirmation_rsvp", "confirmation_registry_purchase")
 * @param utm_campaign - The utm_campaign of the marketing campaign (e.g. "explore_other_options", "post_rsvp_hotel_grid", "next_steps")
 * @returns {string} The formatted UTM query string (e.g. "utm_medium=web&utm_source=website_accommodations_page&utm_campaign=explore_other_options")
 */

/**
 * Extracts UTM parameters from a URL string.
 * Returns undefined if the URL is unparseable or contains no UTM params.
 * `utm_medium` values outside `UTM_MEDIUMS` are dropped.
 *
 * @param url - The URL to extract UTM parameters from
 * @returns Partial UTM parameters object, or undefined if no recognized UTMs exist
 */
export const extractUtmParametersFromUrl = (url: string): PartialUtmParameters | undefined => {
  try {
    const parsed = new URL(url);
    const utm_medium = parsed.searchParams.get('utm_medium');
    const utm_source = parsed.searchParams.get('utm_source');
    const utm_campaign = parsed.searchParams.get('utm_campaign');
    const utm_source_secondary = parsed.searchParams.get('utm_source_secondary');
    const utm_content = parsed.searchParams.get('utm_content');

    const result: PartialUtmParameters = {
      ...(isUtmMedium(utm_medium) ? { utm_medium } : {}),
      ...(utm_source && { utm_source }),
      ...(utm_campaign && { utm_campaign }),
      ...(utm_source_secondary && { utm_source_secondary }),
      ...(utm_content && { utm_content }),
    };
    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Applies UTM parameters to a URL string.
 * Merges UTM parameters into the target URL, preserving existing query parameters.
 *
 * @param url - The target URL to apply UTM parameters to
 * @param utm - Partial UTM parameters to apply
 * @returns The URL with UTM parameters merged in
 */
export const applyUtmParametersToUrl = (url: string, utm: WritableUtmParameters): string => {
  // Relative paths are parsed against a throwaway base so they get the same `set()` (replace)
  // semantics as absolute URLs. The old fallback concatenated blindly, so a path that already
  // carried UTM — every guest-site nav link, since `goToPath` forwards `location.search` — grew a
  // duplicate of each key on every hop, and `getValueString` then read them back as "web,web".
  const RELATIVE_BASE = 'https://relative.invalid';
  // `//host/path` is protocol-relative — an absolute URL missing only its scheme. Parsing it against
  // the base would silently swallow the host, so lend it a scheme and restore the `//` on the way out.
  const isProtocolRelative = url.startsWith('//');
  let urlObj: URL;
  let isPathOnly = false;

  try {
    urlObj = new URL(isProtocolRelative ? `https:${url}` : url);
  } catch {
    try {
      urlObj = new URL(url, RELATIVE_BASE);
      isPathOnly = true;
    } catch {
      return url;
    }
  }

  if (utm.utm_medium) {
    urlObj.searchParams.set('utm_medium', utm.utm_medium);
  }
  if (utm.utm_source) {
    urlObj.searchParams.set('utm_source', utm.utm_source);
  }
  if (utm.utm_campaign) {
    urlObj.searchParams.set('utm_campaign', utm.utm_campaign);
  }
  // Written here, unlike `utm_source_secondary`: a rebuilt path is the one place an inbound
  // `utm_content` can be lost, and the landing page view is what the arm is measured on (TRAV-4212).
  if (utm.utm_content) {
    urlObj.searchParams.set('utm_content', utm.utm_content);
  }

  if (isPathOnly) {
    return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  }
  if (isProtocolRelative) {
    return `//${urlObj.host}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  }
  return urlObj.toString();
};

/** The UTM slots Amplitude can group by — the same list the guest site copies in `useGuestSiteQueryParams`. */
export const UTM_TELEMETRY_KEYS: ReadonlyArray<UtmTelemetryKey> = ['utm_medium', 'utm_campaign', 'utm_source', 'utm_content'];

/**
 * Keeps only the slots that arrived as a real value.
 *
 * A repeated param arrives as an array and is not a value, and an absent slot is left out rather
 * than sent as an empty string — an empty string is a value Amplitude would group by.
 */
export const pickUtmTelemetryParams = (queryParams: Record<string, unknown>): UtmTelemetryParams =>
  UTM_TELEMETRY_KEYS.reduce<Partial<Record<UtmTelemetryKey, string>>>((acc, key) => {
    const value = queryParams[key];
    if (typeof value === 'string' && value !== '' && value !== 'null') {
      acc[key] = value;
    }
    return acc;
  }, {});
